/**
 * "N waiting on the team" early-surface badge (Specs/submission-pack-spec.md P3;
 * renamed from "needs from you" by the status-words plan, D1).
 *
 * A neutral pill, not an accent one: the accent means "AI" or "interactive
 * selection", and this is a count of open questions for people. It sits in the
 * question and document editors' leading header groups, so it is
 * `shrink-0 whitespace-nowrap` like the status pill — see the F24 wrapping
 * contract in DraftEditor. The section editor shows its own section-only
 * to-do count in the footer instead (DraftStatsFooter).
 *
 * Two views, one list — never a second list. This is a COUNT and a LINK: the
 * asks themselves live only on the Submission Pack page, and the badge's whole
 * job is to say "there are N of them" somewhere the writer already is (an
 * editor header, the plan card) and hand them the route.
 *
 * The pack store is global and keyed by bid, so the badge reads the count only
 * while the store is pointing at ITS bid — a pack left over from another bid
 * must never be shown as this one's. It loads the pack when the store holds
 * nothing for this bid (the same guard `PackSnapshot` uses), and skips the
 * fetch when one is already in flight so two mounts on one page (an editor
 * header + the plan card) share a single GET.
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { HelpCircle } from 'lucide-react'
import { selectNeedsFromYouCount, useSubmissionPackStore } from '@/stores/submission-pack-store'

export function NeedsFromYouBadge({ bidId }: { bidId: string | null | undefined }) {
  const storeBidId = useSubmissionPackStore((s) => s.bidId)
  const count = useSubmissionPackStore(selectNeedsFromYouCount)

  useEffect(() => {
    if (!bidId) return
    // Read fresh state rather than the rendered selectors: both mounts commit
    // their effects in the same pass, and `load` raises `isLoading`
    // synchronously, so this is what stops the second one firing a duplicate.
    const state = useSubmissionPackStore.getState()
    if (state.bidId === bidId && (state.pack !== null || state.isLoading)) return
    void state.load(bidId)
  }, [bidId])

  if (!bidId || storeBidId !== bidId || count === 0) return null

  return (
    <Link
      to={`/app/bids/${bidId}/submission-pack`}
      aria-label={`${count} item${count === 1 ? '' : 's'} waiting on the team in the Submission Pack`}
      className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:border-muted-foreground/40 hover:text-foreground"
    >
      <HelpCircle aria-hidden className="h-3 w-3 shrink-0" />
      {count} waiting on the team
    </Link>
  )
}
